import React from 'react';
import { FiMail } from 'react-icons/fi';
import MoneyDisplay from '../../components/MoneyDisplay';
import CurrencyBadge from '../../components/CurrencyBadge';
import TotalsByCurrency from '../../components/TotalsByCurrency';
import { formatMoney } from '../../utils/money';
import {
  AGING_OPTIONS,
  agingLabel,
  agingTone,
  cobranzaWhatsappText,
  estadoPagoLabel,
  isFullyPaid,
  isPendiente,
  invoiceId,
  whatsappHref,
} from '../../utils/cobranzas';
import {
  CATEGORIAS_VENTA,
  formatFecha,
  isQuickSale,
  saleTypeBadgeClass,
  pagoBadgeClass,
} from './facturasPageUtils';

export default function FacturasListPanel(props) {
  const {
    readOnly,
    modoOscuro,
    box,
    textPri,
    textSec,
    textMuted,
    divide,
    btnOut,
    inputBg,
    pillIdle,
    kpis,
    totalesPorMoneda,
    totalEquivalenteArs,
    facturasFiltradas,
    facturas,
    clientesUnicos,
    busqueda,
    handleBusqueda,
    filtroCliente,
    setFiltroCliente,
    filtroFechaDesde,
    setFiltroFechaDesde,
    filtroFechaHasta,
    setFiltroFechaHasta,
    filtroEstado,
    setFiltroEstado,
    filtroAging,
    setFiltroAging,
    filtroCategoria,
    setFiltroCategoria,
    setBusqueda,
    goToCreateQuick,
    handleDuplicarUltima,
    fetchFacturas,
    isFetching,
    exportarVentasExcel,
    exportingCsv,
    hasProfesional,
    mpFileInputRef,
    importarMercadoPago,
    importingMp,
    impagasElegibles,
    enviarRecordatoriosMasivos,
    bulkRemindersSending,
    openDetalle,
    handleEditar,
    handleDuplicar,
    descargarPDF,
    cambiarEstadoPago,
    menuOpenId,
    setMenuOpenId,
    handleEliminar,
    enviarFacturaPorEmail,
    enviarRecordatorioPago,
    agregarGestion,
    whatsappOptions,
  } = props;

  return (
        <section className={`rounded-2xl border overflow-hidden ${box}`}>
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Ventas', value: String(kpis.cantidad) },
              { label: 'Cobrado', value: formatMoney(kpis.cobrado, 'ARS') },
              { label: 'Pendiente', value: formatMoney(kpis.pendiente, 'ARS') },
              { label: 'Clientes', value: String(kpis.clientes) },
            ].map((card, i) => (
              <div
                key={card.label}
                className={`px-3 py-2 ${i > 0 ? (modoOscuro ? 'lg:border-l border-gray-800' : 'lg:border-l border-gray-100') : ''} ${i < 2 ? (modoOscuro ? 'border-b lg:border-b-0 border-gray-800' : 'border-b lg:border-b-0 border-gray-100') : ''}`}
              >
                <div className={`text-[10px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
                <div className={`text-sm font-semibold truncate ${textPri}`}>{card.value}</div>
              </div>
            ))}
          </div>

          <div className={`px-3 py-2 border-t border-b space-y-2 ${divide}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className={`text-sm font-semibold ${textPri}`}>Listado</h2>
                <TotalsByCurrency
                  totals={totalesPorMoneda}
                  equivalenteArs={Object.keys(totalesPorMoneda).some((m) => m !== 'ARS') ? totalEquivalenteArs : null}
                  modoOscuro={modoOscuro}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <>
                    <button
                      type="button"
                      onClick={() => goToCreateQuick}
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white hover:bg-[#1a6fe0]"
                    >
                      Venta rápida
                    </button>
                    <button
                      type="button"
                      disabled={!facturas.length}
                      onClick={handleDuplicarUltima}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                    >
                      Duplicar última
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={fetchFacturas}
                  disabled={isFetching}
                  className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                  title="Actualizar"
                >
                  {isFetching ? '…' : 'Actualizar'}
                </button>
                <button
                  type="button"
                  onClick={exportarVentasExcel}
                  disabled={exportingCsv}
                  className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                >
                  {exportingCsv ? 'Exportando…' : 'Excel'}
                </button>
                {hasProfesional && !readOnly && (
                  <>
                    <input
                      ref={mpFileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={importarMercadoPago}
                    />
                    <button
                      type="button"
                      onClick={() => mpFileInputRef.current?.click()}
                      disabled={importingMp}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                      title="Conciliar o importar reporte CSV de Mercado Pago"
                    >
                      {importingMp ? 'Importando…' : 'MP'}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              <input
                type="search"
                placeholder="Buscar número, cliente o país"
                value={busqueda}
                onChange={handleBusqueda}
                className={`lg:col-span-2 px-3 py-1.5 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
              />
              <select
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${inputBg}`}
              >
                <option value="">Todos los clientes</option>
                {clientesUnicos.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="date"
                value={filtroFechaDesde}
                onChange={(e) => setFiltroFechaDesde(e.target.value)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${inputBg}`}
                title="Desde"
              />
              <input
                type="date"
                value={filtroFechaHasta}
                onChange={(e) => setFiltroFechaHasta(e.target.value)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${inputBg}`}
                title="Hasta"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {[
                { id: 'todas', label: 'Todas' },
                { id: 'pagada', label: 'Pagadas' },
                { id: 'impaga', label: 'Impagas' },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setFiltroEstado(s.id);
                    if (s.id !== 'impaga') setFiltroAging('todas');
                  }}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                    filtroEstado === s.id ? 'bg-[#1F80FF] text-white' : pillIdle
                  }`}
                >
                  {s.label}
                </button>
              ))}
              {filtroEstado === 'impaga' && AGING_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setFiltroAging(s.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                    filtroAging === s.id ? 'bg-[#1F80FF] text-white' : pillIdle
                  }`}
                >
                  {s.label}
                </button>
              ))}
              {CATEGORIAS_VENTA.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFiltroCategoria(c === filtroCategoria ? '' : c)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                    filtroCategoria === c ? 'bg-[#1F80FF] text-white' : pillIdle
                  }`}
                >
                  {c}
                </button>
              ))}
              {hasProfesional && !readOnly && impagasElegibles.length > 0 && (
                <button
                  type="button"
                  onClick={enviarRecordatoriosMasivos}
                  disabled={bulkRemindersSending}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold disabled:opacity-50 ${
                    modoOscuro ? 'bg-amber-900/40 text-amber-200 hover:bg-amber-900/60' : 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100'
                  }`}
                  title="Enviar email de cobranza a todas las impagas con email"
                >
                  <FiMail className={`w-3 h-3 ${bulkRemindersSending ? 'animate-pulse' : ''}`} />
                  {bulkRemindersSending ? 'Enviando…' : `Recordar impagas (${impagasElegibles.length})`}
                </button>
              )}
              {(filtroCategoria || filtroFechaDesde || filtroFechaHasta || filtroCliente || busqueda || filtroEstado !== 'todas' || filtroAging !== 'todas') && (
                <button
                  type="button"
                  onClick={() => {
                    setFiltroCategoria('');
                    setFiltroFechaDesde('');
                    setFiltroFechaHasta('');
                    setFiltroCliente('');
                    setBusqueda('');
                    setFiltroEstado('todas');
                    setFiltroAging('todas');
                  }}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border ${btnOut}`}
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>

        {facturasFiltradas.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className={`text-sm font-medium ${textPri}`}>
              {busqueda || filtroCategoria || filtroFechaDesde || filtroFechaHasta || filtroCliente || filtroEstado !== 'todas' || filtroAging !== 'todas'
                ? 'No hay ventas en este recorte'
                : 'Todavía no hay registros de venta'}
            </p>
            <button
              type="button"
              onClick={() => goToCreateQuick}
              className="mt-3 inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white"
            >
              Cargar primera venta
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 p-3 md:hidden">
              {facturasFiltradas.map((f, idx) => (
                <div key={f._id || idx} className={`rounded-xl border p-3 ${modoOscuro ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${textPri}`}>{f.razonSocial}</p>
                      <p className={`text-[11px] ${textMuted}`}>
                        #{f.numero} · {formatFecha(f.fecha)}
                        {f.categoria ? ` · ${f.categoria}` : ''}
                        {f.pais ? ` · ${f.pais}` : ''}
                      </p>
                      <span className={`mt-1 inline-flex rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${saleTypeBadgeClass(f, modoOscuro)}`}>
                        {isQuickSale(f) ? 'Venta rápida' : 'Venta completa'}
                      </span>
                      {isPendiente(f) && agingLabel(f) && (
                        <span className={`ml-1 mt-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${agingTone(f, modoOscuro)}`}>
                          {agingLabel(f)}
                        </span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${pagoBadgeClass(f, modoOscuro)}`}>
                        {estadoPagoLabel(f)}
                      </span>
                      <div className="mt-1">
                        <MoneyDisplay
                          amount={f.total}
                          currency={f.moneda || 'ARS'}
                          arsAmount={f.montoArs}
                          tipoCambio={f.tipoCambio}
                          modoOscuro={modoOscuro}
                          align="right"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button type="button" onClick={() => openDetalle(f)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Ver</button>
                    {!readOnly && (
                      <>
                        <button type="button" onClick={() => handleEditar(idx)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Editar</button>
                        <button type="button" onClick={() => handleDuplicar(f)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Duplicar</button>
                      </>
                    )}
                    <button type="button" onClick={() => descargarPDF(f._id, f.numero)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>PDF</button>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => cambiarEstadoPago(invoiceId(f), isFullyPaid(f) ? 'impaga' : 'pagada')}
                        className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}
                      >
                        {isFullyPaid(f) ? 'Impaga' : 'Pagada'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                    <th className="text-left font-semibold py-2.5 px-4">N°</th>
                    <th className="text-left font-semibold py-2.5 px-3">Cliente</th>
                    <th className="text-left font-semibold py-2.5 px-3">Fecha</th>
                    <th className="text-left font-semibold py-2.5 px-3">Tipo</th>
                    <th className="text-left font-semibold py-2.5 px-3">Cat.</th>
                    <th className="text-center font-semibold py-2.5 px-3">Moneda</th>
                    <th className="text-right font-semibold py-2.5 px-3">Total</th>
                    <th className="text-center font-semibold py-2.5 px-3">Estado</th>
                    <th className="text-right font-semibold py-2.5 px-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {facturasFiltradas.map((f, idx) => (
                    <tr key={f._id || idx} className={`border-t ${modoOscuro ? 'border-gray-800 hover:bg-gray-800/70' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className={`py-2.5 px-4 text-sm font-mono font-medium ${textPri}`}>#{f.numero}</td>
                      <td className="py-2.5 px-3">
                        <div className={`text-sm font-medium ${textPri}`}>{f.razonSocial}</div>
                        {f.pais && <div className={`text-[11px] ${textMuted}`}>{f.pais}</div>}
                      </td>
                      <td className={`py-2.5 px-3 text-sm ${textSec}`}>{formatFecha(f.fecha)}</td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${saleTypeBadgeClass(f, modoOscuro)}`}>
                          {isQuickSale(f) ? 'Rápida' : 'Completa'}
                        </span>
                      </td>
                      <td className={`py-2.5 px-3 text-sm ${textSec}`}>{f.categoria || '—'}</td>
                      <td className="py-2.5 px-3 text-center">
                        <CurrencyBadge currency={f.moneda || 'ARS'} modoOscuro={modoOscuro} size="xs" />
                      </td>
                      <td className="py-2.5 px-3">
                        <MoneyDisplay
                          amount={f.total}
                          currency={f.moneda || 'ARS'}
                          arsAmount={f.montoArs}
                          tipoCambio={f.tipoCambio}
                          modoOscuro={modoOscuro}
                        />
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="inline-flex flex-col items-center gap-1">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${pagoBadgeClass(f, modoOscuro)}`}>
                            {estadoPagoLabel(f)}
                          </span>
                          {isPendiente(f) && agingLabel(f) && (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${agingTone(f, modoOscuro)}`}>
                              {agingLabel(f)}
                            </span>
                          )}
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => cambiarEstadoPago(invoiceId(f), isFullyPaid(f) ? 'impaga' : 'pagada')}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border ${btnOut}`}
                            >
                              {isFullyPaid(f) ? 'Marcar impaga' : 'Marcar pagada'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openDetalle(f)}
                            className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}
                          >
                            Ver
                          </button>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setMenuOpenId(menuOpenId === f._id ? null : f._id)}
                              className={`px-2 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}
                              title="Más"
                            >
                              ⋯
                            </button>
                            {menuOpenId === f._id && (
                              <div className={`absolute right-0 ${idx >= facturasFiltradas.length - 2 ? 'bottom-full mb-2' : 'mt-2'} w-44 border rounded-lg shadow-xl z-50 text-left ${box}`}>
                                {!readOnly && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => { setMenuOpenId(null); handleEditar(idx); }}
                                      className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'}`}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setMenuOpenId(null); handleDuplicar(f); }}
                                      className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'}`}
                                    >
                                      Duplicar
                                    </button>
                                  </>
                                )}
                                <button
                                  type="button"
                                  onClick={() => { setMenuOpenId(null); descargarPDF(f._id, f.numero); }}
                                  className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'}`}
                                >
                                  Descargar PDF
                                </button>
                                {!readOnly && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => { setMenuOpenId(null); enviarFacturaPorEmail(f._id); }}
                                      className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'}`}
                                    >
                                      Enviar por email
                                    </button>
                                    {isPendiente(f) && hasProfesional && (
                                      <button
                                        type="button"
                                        onClick={() => { setMenuOpenId(null); enviarRecordatorioPago(f._id); }}
                                        className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'}`}
                                      >
                                        Recordatorio de pago
                                      </button>
                                    )}
                                    {isPendiente(f) && whatsappHref(f.telefono, cobranzaWhatsappText(f, whatsappOptions)) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setMenuOpenId(null);
                                          window.open(whatsappHref(f.telefono, cobranzaWhatsappText(f, whatsappOptions)), '_blank', 'noopener,noreferrer');
                                          agregarGestion(f._id, { tipo: 'whatsapp', texto: 'Mensaje de WhatsApp abierto' }).catch(() => {});
                                        }}
                                        className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'}`}
                                      >
                                        WhatsApp
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => { setMenuOpenId(null); handleEliminar(f._id); }}
                                      className={`w-full px-3 py-2 text-xs text-left ${modoOscuro ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}`}
                                    >
                                      Eliminar
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        </section>
  );
}
